import MDinput from '@/components/MDinput';
import InputNumber from '@/components/DSE/InputNumber';
import Select from '@/components/DSE/Select';
import Switch from '@/components/DSE/Switch';
import { validatorRequire, validatorConditionalRequired } from '@/utils/validators';
import { FinishTypeOption, ProductGroupTypeOption } from '@/utils/constants';
import CrudLinkDomainProductGroupFinish from './finishes/index';
import CrudInvalidFinishCombination from './CrudInvalidFinishCombination';
import CrudLinkPriceListItems from './linkpricelistitems/index';

export function getFormDefinition(vue) {
  return {
    type: 'tab',
    saveButtons: [true, false, false, false, false], // Page, tab 1, tab 2, ...
    showFormLabels: [true, false, false, false], // Tab 1, tab 2, ....
    labels: [
      'domain.productGroup',
      'domain.productGroupFinishes',
      'domain.invalidFinishCombinations',
      'domain.productGroupPrices'
    ],
    headerTemplate: (str, form) => `
    <h3>
     ${vue.$i18n.t('domain.productGroupSettings', {
       domainName: vue.domainName,
       productGroup: vue.productGroupDescription
     })}
    </h3>
    `,
    groups: [
      {
        productGroupDescription: {
          type: MDinput,
          props: {
            caption: 'common.description',
            maxlength: 64,
            disabled: true
          },
          defaultValue: vue.productGroupDescription,
          rules: [{ validator: validatorRequire }]
        },
        defaultParcelTypeID: {
          type: Select,
          defaultValue: null,
          props: {
            caption: 'productGroup.parcelType',
            required: true,
            valueField: 'id',
            labelField: 'description',
            loading: vue.$store.getters['parcelType/isLoading'],
            options: vue.$store.getters['parcelType/loadedItems']
          }
        },
        defaultSideFinishID: {
          type: Select,
          defaultValue: null,
          props: {
            valueField: 'id',
            labelField: 'description',
            loading: vue.$store.getters['finish/isLoading'],
            options: vue.$store.getters['finish/loadedItems'].filter(
              finish => finish.finishTypeOption === FinishTypeOption.Side
            )
          }
        },
        defaultSideFinishMeasurementInCm: {
          type: InputNumber,
          defaultValue: null,
          props: {
            caption: 'common.defaultSideFinishMeasurementInCm',
            step: 0.5,
            precision: 1
          },
          valueOnFormChange: (form, oldValue) => {
            if (!form.defaultSideFinishID) {
              return null;
            }
            return oldValue;
          },
          dynamicProps: form => {
            return {
              visible: !!form.defaultSideFinishID
            };
          },
          dynamicRules: form => [{ validator: validatorConditionalRequired(form.defaultSideFinishID) }]
        },
        defaultBottomFinishID: {
          type: Select,
          defaultValue: null,
          props: {
            valueField: 'id',
            labelField: 'description',
            loading: vue.$store.getters['finish/isLoading'],
            options: vue.$store.getters['finish/loadedItems'].filter(
              finish => finish.finishTypeOption === FinishTypeOption.Bottom
            )
          }
        },
        defaultBottomFinishMeasurementInCm: {
          type: InputNumber,
          defaultValue: null,
          props: {
            caption: 'common.defaultBottomFinishMeasurementInCm',
            precision: 1,
            step: 0.5
          },
          valueOnFormChange: (form, oldValue) => {
            if (!form.defaultBottomFinishID) {
              return null;
            }
            return oldValue;
          },
          dynamicProps: form => {
            return {
              visible: !!form.defaultBottomFinishID
            };
          },
          dynamicRules: form => [{ validator: validatorConditionalRequired(form.defaultBottomFinishID) }]
        },
        defaultExtraFinishID: {
          type: Select,
          defaultValue: null,
          props: {
            valueField: 'id',
            labelField: 'description',
            loading: vue.$store.getters['finish/isLoading'],
            options: vue.$store.getters['finish/loadedItems'].filter(
              finish => finish.finishTypeOption === FinishTypeOption.Extra
            )
          }
        },
        defaultLiningBottomFinishID: {
          type: Select,
          defaultValue: null,
          props: {
            valueField: 'id',
            labelField: 'description',
            loading: vue.$store.getters['finish/isLoading'],
            options: vue.$store.getters['finish/loadedItems'].filter(
              finish => finish.finishTypeOption === FinishTypeOption.Bottom
            )
          }
        },
        defaultLiningBottomFinishMeasurementInCm: {
          type: InputNumber,
          defaultValue: null,
          props: {
            precision: 1,
            step: 0.5
          },
          valueOnFormChange: (form, oldValue) => {
            if (!form.defaultLiningBottomFinishID) {
              return null;
            }
            return oldValue;
          },
          dynamicProps: form => {
            return {
              visible: !!form.defaultLiningBottomFinishID
            };
          },
          dynamicRules: form => [{ validator: validatorConditionalRequired(form.defaultLiningBottomFinishID) }]
        },
        defaultEmbraceFinishID: {
          type: Select,
          defaultValue: null,
          props: {
            valueField: 'id',
            labelField: 'description',
            loading: vue.$store.getters['finish/isLoading'],
            options: vue.$store.getters['finish/loadedItems']
          }
        },
        defaultWeightsYN: {
          type: Switch,
          defaultValue: false,
          props: {
            caption: 'productGroup.addWeights',
            activeText: 'common.yes',
            inactiveText: 'common.no'
          }
        },
        minimumNumberOfPanels: {
          type: InputNumber,
          defaultValue: undefined,
          props: {
            caption: 'productGroup.minimumNumberOfPanels',
            precision: 1,
            step: 0.5
          },
          dynamicProps: form => {
            return {
              visible: vue.isProductGroupType([ProductGroupTypeOption.Curtain])
            };
          }
        },
        maximumNumberOfPanels: {
          type: InputNumber,
          defaultValue: undefined,
          props: {
            caption: 'productGroup.maximumNumberOfPanels',
            precision: 1,
            step: 0.5
          },
          dynamicProps: form => {
            return {
              visible: vue.isProductGroupType([ProductGroupTypeOption.Curtain])
            };
          }
        },
        maximumNumberOfLinedPanels: {
          type: InputNumber,
          defaultValue: undefined,
          props: {
            caption: 'productGroup.maximumNumberOfLinedPanels',
            precision: 1,
            step: 0.5
          },
          dynamicProps: form => {
            return {
              visible: vue.isProductGroupType([ProductGroupTypeOption.Curtain])
            };
          }
        },
        minimumWidthInCm: {
          type: InputNumber,
          defaultValue: undefined,
          props: {
            caption: 'productGroup.minimumWidthInCm',
            precision: 1,
            step: 0.5
          }
        },
        maximumWidthInCm: {
          type: InputNumber,
          defaultValue: undefined,
          props: {
            caption: 'productGroup.maximumWidthInCm',
            precision: 1,
            step: 0.5
          },
          dynamicProps: form => {
            return {
              visible: !vue.isProductGroupType([ProductGroupTypeOption.Curtain, ProductGroupTypeOption.NetCurtain])
            };
          }
        },
        maximumLinedWidthInCm: {
          type: InputNumber,
          defaultValue: undefined,
          props: {
            caption: 'productGroup.maximumLinedWidthInCm',
            precision: 1,
            step: 0.5
          },
          dynamicProps: form => {
            return {
              visible: vue.isProductGroupType([ProductGroupTypeOption.RomanBlind])
            };
          }
        },
        maximumFabricWidthInCm: {
          type: InputNumber,
          defaultValue: undefined,
          props: {
            caption: 'productGroup.maximumFabricWidthInCm',
            precision: 1,
            step: 0.5
          },
          dynamicProps: form => {
            return {
              visible: vue.isProductGroupType([ProductGroupTypeOption.Curtain, ProductGroupTypeOption.NetCurtain])
            };
          }
        },
        maximumLinedFabricWidthInCm: {
          type: InputNumber,
          defaultValue: undefined,
          props: {
            caption: 'productGroup.maximumLinedFabricWidthInCm',
            precision: 1,
            step: 0.5
          },
          dynamicProps: form => {
            return {
              visible: vue.isProductGroupType([ProductGroupTypeOption.Curtain])
            };
          }
        },
        minimumHeightInCm: {
          type: InputNumber,
          defaultValue: undefined,
          props: {
            caption: 'productGroup.minimumHeightInCm',
            precision: 1,
            step: 0.5
          }
        },
        maximumHeightInCm: {
          type: InputNumber,
          defaultValue: undefined,
          props: {
            caption: 'productGroup.maximumHeightInCm',
            precision: 1,
            step: 0.5
          },
          dynamicProps: form => {
            return {
              visible: !vue.isProductGroupType([ProductGroupTypeOption.NetCurtain])
            };
          }
        },
        maximumLinedHeightInCm: {
          type: InputNumber,
          defaultValue: undefined,
          props: {
            caption: 'productGroup.maximumLinedHeightInCm',
            precision: 1,
            step: 0.5
          },
          dynamicProps: form => {
            return {
              visible: vue.isProductGroupType([ProductGroupTypeOption.RomanBlind])
            };
          }
        },
        activeYN: {
          type: Switch,
          defaultValue: true,
          props: {
            caption: 'common.active',
            activeText: 'common.yes',
            inactiveText: 'common.no'
          }
        },
        productGroupFinishes: {
          type: CrudLinkDomainProductGroupFinish,
          props: {
            caption: 'common.emptyString',
            parentIdentifiers: { productGroupID: +vue.productGroupID, domainID: +vue.domainID }
          }
        },
        linkPriceListItems: {
          type: CrudLinkPriceListItems,
          defaultValue: [],
          props: {
            caption: 'common.emptyString',
            parentIdentifiers: { productGroupID: +vue.productGroupID, domainID: +vue.domainID }
          }
        },
        invalidFinishCombinations: {
          type: CrudInvalidFinishCombination,
          props: {
            caption: 'common.emptyString',
            parentIdentifiers: { productGroupID: vue.productGroupID, domainID: vue.domainID }
          }
        }
      }
    ]
  };
}

const layoutTabGeneral = {
  named: true,
  sections: [
    {
      fields: [
        'productGroupDescription',
        'defaultParcelTypeID',
        'defaultSideFinishID',
        'defaultSideFinishMeasurementInCm',
        'defaultBottomFinishID',
        'defaultBottomFinishMeasurementInCm',
        'defaultLiningBottomFinishID',
        'defaultLiningBottomFinishMeasurementInCm',
        'defaultExtraFinishID',
        'defaultEmbraceFinishID',
        'defaultWeightsYN',
        'minimumNumberOfPanels',
        'maximumNumberOfPanels',
        'maximumNumberOfLinedPanels',
        'minimumWidthInCm',
        'maximumWidthInCm',
        'maximumLinedWidthInCm',
        'maximumFabricWidthInCm',
        'maximumLinedFabricWidthInCm',
        'minimumHeightInCm',
        'maximumHeightInCm',
        'maximumLinedHeightInCm',
        'activeYN'
      ]
    }
  ]
};
const layoutTabProductGroupFinishes = {
  named: true,
  sections: [
    {
      fields: ['productGroupFinishes']
    }
  ]
};

const layoutTabInvalidFinishCombinations = {
  named: true,
  sections: [
    {
      fields: ['invalidFinishCombinations']
    }
  ]
};

const layoutTabProductGroupPrices = {
  named: true,
  sections: [
    {
      fields: ['linkPriceListItems']
    }
  ]
};

export function getCreateLayoutDefinition(vue) {
  return {
    groups: [
      layoutTabGeneral,
      layoutTabProductGroupFinishes,
      layoutTabInvalidFinishCombinations,
      layoutTabProductGroupPrices
    ]
  };
}

export function getEditLayoutDefinition(vue) {
  return {
    groups: [
      layoutTabGeneral,
      layoutTabProductGroupFinishes,
      layoutTabInvalidFinishCombinations,
      layoutTabProductGroupPrices
    ]
  };
}

export function createItemFromFormData(vue, form) {
  return form;
}

export function createFormDataFromItem(vue, item) {
  const form = {
    ...item
  };
  if (item.minimumNumberOfPanels === null) {
    form.minimumNumberOfPanels = undefined;
  }
  if (item.maximumNumberOfPanels === null) {
    form.maximumNumberOfPanels = undefined;
  }
  if (item.maximumNumberOfLinedPanels === null) {
    form.maximumNumberOfLinedPanels = undefined;
  }
  if (item.minimumWidthInCm === null) {
    form.minimumWidthInCm = undefined;
  }
  if (item.maximumWidthInCm === null) {
    form.maximumWidthInCm = undefined;
  }
  if (item.maximumLinedWidthInCm === null) {
    form.maximumLinedWidthInCm = undefined;
  }
  if (item.maximumFabricWidthInCm === null) {
    form.maximumFabricWidthInCm = undefined;
  }
  if (item.maximumLinedFabricWidthInCm === null) {
    form.maximumLinedFabricWidthInCm = undefined;
  }
  if (item.minimumHeightInCm === null) {
    form.minimumHeightInCm = undefined;
  }
  if (item.maximumHeightInCm === null) {
    form.maximumHeightInCm = undefined;
  }
  if (item.maximumLinedHeightInCm === null) {
    form.maximumLinedHeightInCm = undefined;
  }
  return form;
}
